import {
  type GatewayInstance,
  type ServerDetails,
  type ServerRegistration,
  serverIpv4Pattern,
  serverRegions,
} from '@/services/business/servers/types';
import { accountStore } from '../store/account';
import { gatewayInstances, gatewayServers } from './fixtures';

export class ServerRegistrationError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}
interface OwnedRegistration {
  ownerId: string;
  value: ServerRegistration;
}
const registrations = new Map<string, OwnedRegistration>();
let sequence = 0;
export function resetServerRegistrations() {
  for (let index = gatewayServers.length - 1; index >= 0; index--)
    if (gatewayServers[index].id.startsWith('srv-managed-'))
      gatewayServers.splice(index, 1);
  for (let index = gatewayInstances.length - 1; index >= 0; index--)
    if (gatewayInstances[index].id.startsWith('gateway-managed-'))
      gatewayInstances.splice(index, 1);
  registrations.clear();
  sequence = 0;
}
function serverById(id: string) {
  const server = gatewayServers.find((item) => item.id === id);
  if (!server) throw new ServerRegistrationError(404, '服务器不存在或已取消');
  return server;
}
function refreshExpiry(value: ServerRegistration) {
  if (value.state === 'pending' && Date.now() >= Date.parse(value.expiresAt)) {
    value.state = 'expired';
    value.reason = '注册会话已过期，请重新生成';
  }
  return value;
}
export function serverRegistrationFor(id: string) {
  const entry = registrations.get(id);
  return entry?.ownerId === accountStore.profile().id
    ? { ...refreshExpiry(entry.value) }
    : undefined;
}
function owned(id: string, expectedVersion?: unknown, allowExpired = false) {
  const server = serverById(id),
    entry = registrations.get(id);
  if (!entry) throw new ServerRegistrationError(404, '注册会话不存在');
  if (entry.ownerId !== accountStore.profile().id)
    throw new ServerRegistrationError(403, '无权管理该注册会话');
  const registration = refreshExpiry(entry.value);
  if (!allowExpired && registration.state === 'expired')
    throw new ServerRegistrationError(410, registration.reason as string);
  if (
    expectedVersion !== undefined &&
    (!Number.isInteger(expectedVersion) || Number(expectedVersion) < 1)
  )
    throw new ServerRegistrationError(422, '注册版本参数无效');
  if (expectedVersion !== undefined && expectedVersion !== registration.version)
    throw new ServerRegistrationError(409, '注册信息已更新，请重新读取');
  return { server, registration };
}
function snapshot(server: ServerDetails): ServerDetails {
  return { ...server, registration: serverRegistrationFor(server.id) };
}
export function addServer(input: Record<string, unknown>) {
  if (
    typeof input.name !== 'string' ||
    !input.name.trim() ||
    input.name.trim().length > 40 ||
    !serverRegions.some((region) => region === input.region) ||
    typeof input.ip !== 'string' ||
    !serverIpv4Pattern.test(input.ip) ||
    (input.specification !== undefined &&
      (typeof input.specification !== 'string' || input.specification.length > 80))
  )
    throw new ServerRegistrationError(422, '请填写有效的名称、地区和 IPv4 地址');
  if (
    gatewayServers.some(
      (server) =>
        server.ip === input.ip ||
        server.name.toLowerCase() === (input.name as string).trim().toLowerCase(),
    )
  )
    throw new ServerRegistrationError(409, '服务器名称或 IP 已存在');
  const id = `srv-managed-${++sequence}`;
  const server: ServerDetails = {
    id,
    name: input.name.trim(),
    region: input.region as ServerDetails['region'],
    ip: input.ip,
    clusterId: 'cluster-primary',
    status: 'unknown',
    cpu: null,
    memory: null,
    disk: null,
    sampledAt: null,
    lastReportedAt: null,
    specification:
      typeof input.specification === 'string' ? input.specification.trim() : '',
    instances: [],
    instanceCount: 0,
    readyCount: 0,
  };
  gatewayServers.unshift(server);
  registrations.set(id, {
    ownerId: accountStore.profile().id,
    value: {
      id: `registration-${++sequence}`,
      state: 'pending',
      expiresAt: new Date(Date.now() + 5 * 60_000).toISOString(),
      version: 1,
      lastCheck: null,
      reason: null,
    },
  });
  return snapshot(server);
}
export function changeRegistration(
  id: string,
  action: 'check' | 'regenerate' | 'cancel',
  expectedVersion: unknown,
) {
  if (expectedVersion === undefined)
    throw new ServerRegistrationError(422, '注册版本参数无效');
  const { server, registration } = owned(id, expectedVersion, action !== 'check');
  if (registration.state === 'registered')
    throw new ServerRegistrationError(409, '服务器已注册，不能重复注册或取消');
  if (action === 'cancel') {
    gatewayServers.splice(gatewayServers.indexOf(server), 1);
    registrations.delete(id);
    return { id };
  }
  if (action === 'regenerate') {
    Object.assign(registration, {
      id: `registration-${++sequence}`,
      state: 'pending',
      expiresAt: new Date(Date.now() + 5 * 60_000).toISOString(),
      lastCheck: null,
      reason: null,
      version: registration.version + 1,
    });
    return snapshot(server);
  }
  registration.lastCheck = new Date().toISOString();
  registration.version++;
  if (server.ip.endsWith('.250') || server.ip.endsWith('.251')) {
    registration.reason = server.ip.endsWith('.250')
      ? '尚未收到 Agent 注册请求，请检查后重试'
      : '注册请求未通过验证，请检查后重试';
    throw new ServerRegistrationError(
      server.ip.endsWith('.250') ? 504 : 403,
      registration.reason,
    );
  }
  registration.state = 'registered';
  registration.reason = null;
  server.status = 'online';
  server.lastReportedAt = registration.lastCheck;
  return snapshot(server);
}
function candidateFor(server: ServerDetails): GatewayInstance {
  return {
    id: `gateway-managed-${server.id}`,
    serverId: server.id,
    status: 'ready',
    desiredVersion: 'v1.8.2',
    appliedVersion: 'v1.8.2',
    inflightRequests: 0,
    lastHeartbeatAt: new Date().toISOString(),
  };
}
export function discoveredInstances(id: string) {
  const { server, registration } = owned(id);
  if (registration.state !== 'registered' || server.status !== 'online')
    throw new ServerRegistrationError(409, '请先完成服务器注册并保持在线');
  const candidate = candidateFor(server);
  return gatewayInstances.some((instance) => instance.id === candidate.id)
    ? []
    : [candidate];
}
export function linkDiscoveredInstance(id: string, input: Record<string, unknown>) {
  if (
    input.expectedVersion === undefined ||
    typeof input.instanceId !== 'string' ||
    'serverId' in input
  )
    throw new ServerRegistrationError(422, '实例关联参数无效');
  const { server, registration } = owned(id, input.expectedVersion);
  if (registration.state !== 'registered' || server.status !== 'online')
    throw new ServerRegistrationError(409, '请先完成服务器注册并保持在线');
  if (gatewayInstances.some((instance) => instance.id === input.instanceId))
    throw new ServerRegistrationError(409, '该实例已被关联');
  const candidate = candidateFor(server);
  if (input.instanceId !== candidate.id)
    throw new ServerRegistrationError(404, '未发现该实例，不能跨服务器关联');
  gatewayInstances.push(candidate);
  server.instances = gatewayInstances.filter((instance) => instance.serverId === id);
  server.instanceCount = server.instances.length;
  server.readyCount = server.instances.filter(
    (instance) => instance.status === 'ready',
  ).length;
  registration.version++;
  return snapshot(server);
}
